import { useState, useRef, useEffect } from 'react';

export const FONT_SIZES = ['12px', '14px', '16px', '18px', '24px', '32px'];
export const FONT_SIZE_OPTIONS = [6,8,10,12,14,16,18,21,24,28,32,36,42,48,56,64,72,80,88,96,104,120,144];

export const lineSpacingOptions = [
  { value: '1', label: '1.0' },
  { value: '1.15', label: '1.15' },
  { value: '1.5', label: '1.5' },
  { value: '2', label: '2.0' },
];

export const toolbarBtn = {
  base: {
    background: 'none',
    border: 'none',
    borderRadius: 10,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    margin: 0,
    padding: 0,
    color: '#6b7280',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
  },
  active: {
    background: 'linear-gradient(135deg, #8a2be2 0%, #a855f7 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(138, 43, 226, 0.25)',
  },
  hover: {
    background: 'linear-gradient(135deg, #f3eafd 0%, #ede2f9 100%)',
    color: '#8a2be2',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(138, 43, 226, 0.15)',
  }
};

export const separatorStyle = {
  width: '1px',
  height: '24px',
  background: 'linear-gradient(180deg, transparent 0%, rgba(138, 43, 226, 0.2) 50%, transparent 100%)',
  margin: '0 4px'
};

export const popularColors = [
  '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
  '#FF0000', '#FF3333', '#FF6666', '#FF9999', '#FFCCCC',
  '#FF6600', '#FF9933', '#FFCC66', '#FFEECC',
  '#FFFF00', '#FFFF33', '#FFFF66', '#FFFF99',
  '#00FF00', '#33FF33', '#66FF66', '#99FF99', '#CCFFCC',
  '#00FFFF', '#33FFFF', '#66FFFF', '#99FFFF', '#CCFFFF',
  '#0000FF', '#3333FF', '#6666FF', '#9999FF', '#CCCCFF',
  '#FF00FF', '#FF33FF', '#FF66FF', '#FF99FF', '#FFCCFF',
  '#8A2BE2', '#9933CC', '#9966CC', '#9999CC', '#CCCCFF',
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'
];

export const pastelHighlightColors = [
  { name: 'yellow', color: '#fef3c7' },
  { name: 'pink', color: '#fce7f3' },
  { name: 'blue', color: '#dbeafe' },
  { name: 'green', color: '#dcfce7' },
  { name: 'purple', color: '#f3e8ff' },
  { name: 'orange', color: '#fed7aa' },
  { name: 'red', color: '#fee2e2' },
  { name: 'teal', color: '#ccfbf1' },
  { name: 'lime', color: '#f7fee7' },
  { name: 'indigo', color: '#e0e7ff' },
  { name: 'rose', color: '#ffe4e6' },
  { name: 'cyan', color: '#cffafe' },
  { name: 'emerald', color: '#d1fae5' },
  { name: 'amber', color: '#fef3c7' },
  { name: 'violet', color: '#ede9fe' }
];

export function useToolbarState({ activeEditor, currentTab, whiteboards }) {
  const [currentAlignment, setCurrentAlignment] = useState('left');
  const [currentLineSpacing, setCurrentLineSpacing] = useState('normal');
  const [showColorPopup, setShowColorPopup] = useState(false);
  const [colorButtonRef, setColorButtonRef] = useState(null);
  const [showHighlightPopup, setShowHighlightPopup] = useState(false);
  const [highlightButtonRef, setHighlightButtonRef] = useState(null);
  const [fontSizeFocused, setFontSizeFocused] = useState(false);
  const [fontSizeDropdownOpen, setFontSizeDropdownOpen] = useState(false);
  const [fontFamilyDropdownOpen, setFontFamilyDropdownOpen] = useState(false);
  const [fontSelectHovered, setFontSelectHovered] = useState(false);
  const fontSizeRef = useRef();
  const [fontSizeDropdownPos, setFontSizeDropdownPos] = useState({ top: 0, left: 0 });
  const [fontSizeHovered, setFontSizeHovered] = useState(false);
  const [fontFamilyDropdownPos, setFontFamilyDropdownPos] = useState({ top: 0, left: 0 });
  const fontFamilyRef = useRef();
  const [hoveredButton, setHoveredButton] = useState(null);
  const [, setForceUpdate] = useState(0);

  useEffect(() => {
    if (!fontSizeDropdownOpen) return;
    function handle(e) {
      if (fontSizeRef.current && !fontSizeRef.current.contains(e.target)) setFontSizeDropdownOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [fontSizeDropdownOpen]);

  useEffect(() => {
    if (!fontFamilyDropdownOpen) return;
    function handle(e) {
      if (fontFamilyRef.current && !fontFamilyRef.current.contains(e.target)) setFontFamilyDropdownOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [fontFamilyDropdownOpen]);

  useEffect(() => {
    window.updateToolbar = () => {
      setForceUpdate(prev => prev + 1);
    };
    return () => {
      delete window.updateToolbar;
    };
  }, []);

  useEffect(() => {
    if (activeEditor && (currentTab === 0 || (currentTab > 0 && (whiteboards[currentTab - 1]?.type === 'doc' || whiteboards[currentTab - 1]?.type === 'homework')))) {
      setForceUpdate(prev => prev + 1);
    }
  }, [activeEditor, currentTab, whiteboards]);

  useEffect(() => {
    const handleBoardClick = (e) => {
      if (currentTab > 0 && whiteboards[currentTab - 1]?.type === 'whiteboard') {
        const textElement = e.target.closest('[data-shape-id]');
        if (textElement) {
          setForceUpdate(prev => prev + 1);
        }
      }
      if (currentTab > 0 && (whiteboards[currentTab - 1]?.type === 'homework' || whiteboards[currentTab - 1]?.type === 'doc')) {
        const editorElement = e.target.closest('.ProseMirror');
        if (editorElement) {
          setForceUpdate(prev => prev + 1);
        }
      }
      if (!e.target.closest('[data-text-id]')) {
        window.selectedImageTextElement = null;
      }
    };
    document.addEventListener('click', handleBoardClick);
    return () => document.removeEventListener('click', handleBoardClick);
  }, [currentTab, whiteboards]);

  useEffect(() => {
    const handleEscape = (e) => {
      const activeElement = document.activeElement;
      if (activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable ||
          activeElement.closest('.lesson-title-input') ||
          activeElement.classList.contains('lesson-title-input')
      )) {
        return;
      }
      if (e.key === 'Escape') {
        setShowColorPopup(false);
        setShowHighlightPopup(false);
        setFontSizeDropdownOpen(false);
        setFontFamilyDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return {
    currentAlignment, setCurrentAlignment,
    currentLineSpacing, setCurrentLineSpacing,
    showColorPopup, setShowColorPopup,
    colorButtonRef, setColorButtonRef,
    showHighlightPopup, setShowHighlightPopup,
    highlightButtonRef, setHighlightButtonRef,
    fontSizeFocused, setFontSizeFocused,
    fontSizeDropdownOpen, setFontSizeDropdownOpen,
    fontFamilyDropdownOpen, setFontFamilyDropdownOpen,
    fontSelectHovered, setFontSelectHovered,
    fontSizeRef,
    fontSizeDropdownPos, setFontSizeDropdownPos,
    fontSizeHovered, setFontSizeHovered,
    fontFamilyDropdownPos, setFontFamilyDropdownPos,
    fontFamilyRef,
    hoveredButton, setHoveredButton,
    setForceUpdate,
  };
}
